'use client';

import React, { useId, useState } from 'react';
import { resolveContentDensityPresentation, type ContentDensityInput } from '@statura/config';
import { ChevronDown } from 'lucide-react';

import { cn } from '../lib/utils';
import {
  EXPERIENCE_FOCUS_CLASS,
  EXPERIENCE_MOTION_CLASS,
  EXPERIENCE_SURFACE_BASE_CLASS,
} from '../lib/experience-semantic-styles';

export interface ContentDensitySectionProps extends ContentDensityInput {
  title: string;
  children: React.ReactNode;
  storageKey: string;
  className?: string;
  initiallyExpanded?: boolean;
  /** Pre-hydration preference supplied by the owning app; avoids post-paint localStorage reflow. */
  initialPreference?: 'expanded' | 'collapsed' | null;
}

export function resolvePreHydrationContentDensityPreference(input: {
  policyDefaultExpanded: boolean;
  canCollapse: boolean;
  initiallyExpanded: boolean;
  initialPreference: 'expanded' | 'collapsed' | null;
}): boolean {
  if (!input.canCollapse || input.policyDefaultExpanded) return true;
  if (input.initialPreference === 'expanded') return true;
  if (input.initialPreference === 'collapsed') return false;
  return input.initiallyExpanded;
}

/** Canonical browser adapter for the cross-surface density contract. */
export function ContentDensitySection({
  title,
  children,
  storageKey,
  className,
  initiallyExpanded = false,
  initialPreference = null,
  ...densityInput
}: ContentDensitySectionProps) {
  const policy = resolveContentDensityPresentation(densityInput);
  const panelId = useId();
  const [expanded, setExpanded] = useState(() =>
    resolvePreHydrationContentDensityPreference({
      policyDefaultExpanded: policy.defaultExpanded,
      canCollapse: policy.canCollapse,
      initiallyExpanded,
      initialPreference,
    }),
  );

  if (!policy.canCollapse) {
    return (
      <section
        className={cn(
          'rounded-xl border border-border bg-surface-base p-4 text-text-body',
          EXPERIENCE_SURFACE_BASE_CLASS,
          className,
        )}
        data-content-density={densityInput.level}
        data-density-presentation={policy.presentation}
      >
        <h2 className="experience-candidate-heading font-heading text-card-title text-text-heading">
          {title}
        </h2>
        <div className="mt-3">{children}</div>
      </section>
    );
  }

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    try {
      window.localStorage.setItem(storageKey, next ? 'expanded' : 'collapsed');
    } catch {
      // Keep the in-memory interaction working if storage is unavailable.
    }
  };

  return (
    <section
      className={cn(
        'overflow-hidden rounded-xl border border-border bg-surface-base text-text-body',
        EXPERIENCE_SURFACE_BASE_CLASS,
        className,
      )}
      data-content-density={densityInput.level}
      data-density-presentation={policy.presentation}
    >
      <h2>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          aria-controls={panelId}
          className={cn(
            'experience-candidate-heading flex w-full items-center justify-between gap-3 px-4 py-3 text-left font-heading text-card-title text-text-heading',
            EXPERIENCE_FOCUS_CLASS,
            EXPERIENCE_MOTION_CLASS,
          )}
        >
          <span>{title}</span>
          <ChevronDown
            className={cn(
              'experience-candidate-motion h-4 w-4 shrink-0 transition-transform duration-200',
              expanded && 'rotate-180',
            )}
            aria-hidden="true"
          />
        </button>
      </h2>
      <div
        id={panelId}
        hidden={!expanded}
        className="experience-candidate-density-panel border-t border-border p-4"
      >
        {children}
      </div>
    </section>
  );
}
